import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
  HeadContent,
  Scripts,
  redirect,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { REVIEW_STATS } from "@/data/reviews";

// Inline de gecompileerde Tailwind-bundle in de SSR-HTML zodat er geen
// render-blocking CSS-request meer nodig is voor het eerste paint.
// Bytes zijn identiek aan de externe stylesheet; alleen het transport
// verandert (inline in <style>-tag i.p.v. losse <link>-request).
import appCss from "../styles.css?inline";
import { reportError } from "../lib/error-reporting";
import { AuthProvider } from "@/hooks/use-auth";
import { CartProvider } from "@/hooks/use-cart";
import { VatProvider } from "@/hooks/use-vat";
import { AnnouncerProvider } from "@/lib/a11y-announcer";
import { CookieBanner } from "@/components/site/CookieBanner";
import { offerShippingDetails, STANDARD_SHIPPING_INCL_CENTS } from "@/lib/shipping-jsonld";

// Niet-kritieke UI-onderdelen: lazy-loaded zodat ze niet in de main-bundle
// terechtkomen. Zichtbaar gedrag is identiek — deze onderdelen renderen
// niets zichtbaar boven-de-vouw (Toaster is een portal, de rest bestaat
// alleen uit useEffect-side-effects).
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
const SeoOverrideApplier = lazy(() =>
  import("@/components/SeoOverrideApplier").then((m) => ({ default: m.SeoOverrideApplier })),
);
const TelClickTracker = lazy(() =>
  import("@/components/site/TelClickTracker").then((m) => ({ default: m.TelClickTracker })),
);
const WebVitalsOverlay = lazy(() =>
  import("@/components/WebVitalsOverlay").then((m) => ({ default: m.WebVitalsOverlay })),
);
const JsonLdValidator = lazy(() =>
  import("@/components/JsonLdValidator").then((m) => ({ default: m.JsonLdValidator })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  const err = error instanceof Error ? error : new Error(String(error));
  console.error(err);
  const router = useRouter();
  useEffect(() => {
    reportError(err, { boundary: "tanstack_root_error_component" });
  }, [err]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Canonicaliseer URL's op de server (SSR) én client:
  // - verwijder trailing slash (behalve "/")
  // - lowercase het pad zodat /Contact 301 → /contact
  // - collapse dubbele slashes (/foo//bar → /foo/bar)
  // Voor `redirect()` uit een root beforeLoad gebruikt TanStack Start
  // een echte 301 tijdens SSR.
  beforeLoad: ({ location }) => {
    const { pathname, search, hash } = location;
    let next = pathname;

    // Collapse dubbele slashes.
    if (next.includes("//")) next = next.replace(/\/{2,}/g, "/");

    // Strip trailing slash (behalve root).
    if (next.length > 1 && next.endsWith("/")) {
      next = next.replace(/\/+$/, "");
    }

    // Lowercase indien er hoofdletters in het pad zitten.
    if (/[A-Z]/.test(next)) {
      next = next.toLowerCase();
    }

    const suffix =
      (search ? `?${new URLSearchParams(search as Record<string, string>).toString()}` : "") +
      (hash ? `#${hash}` : "");

    if (next !== pathname) {
      throw redirect({
        href: next + suffix,
        statusCode: 301,
      });
    }
    return undefined as never;
  },



  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Elvion Group | E-techniek & beveiliging" },
      { name: "description", content: "Elvion Group Nederland: totaalinstallateur voor elektrotechniek, beveiliging, camerabewaking en laadpalen. Betrouwbaar en vakkundig." },
      { name: "author", content: "Elvion Group Nederland" },
      { property: "og:title", content: "Elvion Group | E-techniek & beveiliging" },
      { property: "og:description", content: "Totaalinstallateur voor elektrotechniek, beveiliging, camerabewaking en laadpalen. Betrouwbaar en vakkundig." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Elvion Group Nederland" },
      { property: "og:locale", content: "nl_NL" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Elvion Group | E-techniek & beveiliging" },
      { name: "twitter:description", content: "Totaalinstallateur voor elektrotechniek, beveiliging, camerabewaking en laadpalen." },

    ],
    links: [
      // Tailwind-bundle wordt inline in <style> gerenderd in RootShell,
      // zodat er geen render-blocking CSS-request meer nodig is.
      // Google Translate + gstatic: pas geladen als de gebruiker een taal
      // kiest. Alleen dns-prefetch — preconnect zou een ongebruikte
      // verbinding openen (waarschuwing in PageSpeed).
      { rel: "dns-prefetch", href: "https://translate.google.com" },
      { rel: "dns-prefetch", href: "https://translate.googleapis.com" },
      { rel: "dns-prefetch", href: "https://www.gstatic.com" },
      // images.weserv.nl serveert de responsive/WebP-varianten van product-
      // en hero-afbeeldingen; preconnect scheelt ~150-300 ms bij de LCP.
      { rel: "preconnect", href: "https://images.weserv.nl", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://images.weserv.nl" },
      // SEO Juice suggestions script (via /api/public/vendor/seojuice) doet
      // XHR-calls naar smart.seojuice.io — preconnect scheelt ~300 ms op mobiel.
      { rel: "preconnect", href: "https://smart.seojuice.io", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://smart.seojuice.io" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://elvion-group.nl/#organization",
          name: "Elvion Group Nederland",
          legalName: "Elvion Group Nederland",
          alternateName: "Elvion Group",
          url: "https://elvion-group.nl",
          logo: "https://elvion-group.nl/__l5e/assets-v1/d739c698-1ddc-490a-8f79-ade5094ac4d0/elvion-logo.png",
          image: "https://elvion-group.nl/__l5e/assets-v1/d739c698-1ddc-490a-8f79-ade5094ac4d0/elvion-logo.png",
          description: "Elvion Group Nederland levert hoogwaardige elektrotechnische oplossingen, beveiligings- en netwerkinstallaties voor bedrijven en particulieren.",
          telephone: "+31850806695",
          email: "info@elvion-group.nl",
          taxID: "NL868363753B01",
          vatID: "NL868363753B01",
          identifier: [
            { "@type": "PropertyValue", name: "KvK", value: "98117475" },
            { "@type": "PropertyValue", name: "BTW", value: "NL868363753B01" },
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: "Betsy van Goorstraat 15",
            postalCode: "6702DC",
            addressLocality: "Wageningen",
            addressRegion: "Gelderland",
            addressCountry: "NL",
          },
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+31850806695",
              email: "info@elvion-group.nl",
              contactType: "customer service",
              areaServed: "NL",
              availableLanguage: ["Dutch", "English"],
            },
          ],
          areaServed: [
            { "@type": "Country", name: "Nederland" },
            { "@type": "Country", name: "België" },
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(REVIEW_STATS.totaal.score),
            bestRating: "10",
            worstRating: "1",
            reviewCount: String(REVIEW_STATS.totaal.aantal),
          },
          sameAs: [
            "https://www.linkedin.com/company/elvion-group",
            "https://www.facebook.com/elvion.group",
            "https://www.instagram.com/elvion.group",
            "https://www.marktplaats.nl/smb-profile/profile/58197191",
            "https://www.homedeal.nl/pro/Jj8dM/elvion-group-nederland/",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://elvion-group.nl/#website",
          name: "Elvion Group Nederland",
          url: "https://elvion-group.nl",
          inLanguage: "nl-NL",
          publisher: { "@id": "https://elvion-group.nl/#organization" },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              // Per schema.org / Google-spec MOET {search_term_string} letterlijk
              // in de urlTemplate staan — de search client (Google) past zelf
              // percent-encoding toe op de ingevulde term. Nooit zelf encoden.
              urlTemplate: "https://elvion-group.nl/assortiment?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://elvion-group.nl/#localbusiness",
          name: "Elvion Group Nederland",
          image: "https://elvion-group.nl/__l5e/assets-v1/d739c698-1ddc-490a-8f79-ade5094ac4d0/elvion-logo.png",
          logo: "https://elvion-group.nl/__l5e/assets-v1/d739c698-1ddc-490a-8f79-ade5094ac4d0/elvion-logo.png",
          url: "https://elvion-group.nl",
          telephone: "+31850806695",
          email: "info@elvion-group.nl",
          priceRange: "€€",
          currenciesAccepted: "EUR",
          paymentAccepted: "iDEAL, Bancontact, Creditcard, SEPA, Apple Pay, Google Pay",
          taxID: "NL868363753B01",
          vatID: "NL868363753B01",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Betsy van Goorstraat 15",
            postalCode: "6702DC",
            addressLocality: "Wageningen",
            addressRegion: "Gelderland",
            addressCountry: "NL",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 51.9691,
            longitude: 5.6654,
          },
          hasMap: "https://www.google.com/maps/search/?api=1&query=Elvion+Group+Nederland+Betsy+van+Goorstraat+15+Wageningen",
          areaServed: [
            { "@type": "Country", name: "Nederland" },
            { "@type": "Country", name: "België" },
            { "@type": "City", name: "Amsterdam" },
            { "@type": "City", name: "Rotterdam" },
            { "@type": "City", name: "Den Haag" },
            { "@type": "City", name: "Utrecht" },
            { "@type": "City", name: "Eindhoven" },
            { "@type": "City", name: "Almere" },
            { "@type": "City", name: "Tilburg" },
            { "@type": "City", name: "Nijmegen" },
            { "@type": "City", name: "Arnhem" },
          ],
          serviceArea: {
            "@type": "GeoCircle",
            geoMidpoint: {
              "@type": "GeoCoordinates",
              latitude: 52.1326,
              longitude: 5.2913,
            },
            geoRadius: 150000,
          },
          knowsAbout: [
            "Elektrotechniek",
            "Groepenkasten",
            "Verlichting",
            "Beveiligingscamera's",
            "Alarmsystemen",
            "Intercomsystemen",
            "Netwerkinstallatie",
            "Laadpalen",
            "Thuisbatterijen",
          ],
          makesOffer: [
            {
              "@type": "Offer",
              name: "Levering webshopbestellingen",
              description:
                "Levering binnen Nederland en België. Verzendkosten worden vóór betaling getoond; alleen binnen Nederland gratis verzending vanaf €150.",
              priceCurrency: "EUR",
              eligibleRegion: [
                { "@type": "Country", name: "Nederland" },
                { "@type": "Country", name: "België" },
              ],
              shippingDetails: offerShippingDetails(STANDARD_SHIPPING_INCL_CENTS),
              itemOffered: { "@type": "Service", name: "Bezorging van bestelde producten" },
            },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Groepenkast vervangen" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Beveiligingscamera installatie" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Laadpaal installatie" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Netwerkinstallatie" } },
          ],
          sameAs: [
            "https://www.linkedin.com/company/elvion-group",
            "https://www.facebook.com/elvion.group",
            "https://www.instagram.com/elvion.group",
            "https://www.marktplaats.nl/smb-profile/profile/58197191",
            "https://www.homedeal.nl/pro/Jj8dM/elvion-group-nederland/",
          ],
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+31850806695",
              email: "info@elvion-group.nl",
              contactType: "customer service",
              areaServed: "NL",
              availableLanguage: ["Dutch", "English"],
              url: "https://elvion-group.nl/contact",
            },
          ],
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "08:30",
              closes: "17:00",
            },
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(REVIEW_STATS.totaal.score),
            bestRating: "10",
            worstRating: "1",
            reviewCount: String(REVIEW_STATS.totaal.aantal),
          },
        }),
      },
      {
        // Proxy via eigen worker → lange cache-TTL (upstream stuurt 1 uur,
        // wij serveren 7 dagen immutable). Zie src/routes/api/public/vendor/seojuice.ts
        src: "/api/public/vendor/seojuice",
        defer: true,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});


function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="nl">
      <head>
        <HeadContent />
        {/* Inline Tailwind-bundle — elimineert render-blocking CSS-request. */}
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
        {/*
          De Tailwind-bundle staat hierboven al inline, dus de externe
          Vite-CSS-links (bevatten dezelfde regels) zijn niet nodig om de
          eerste paint te stylen. We flippen ze naar media="print" zodat ze
          niet meer render-blocking zijn, en zetten media terug op "all"
          zodra ze zijn geladen. Puur netwerkgedrag; geen visuele wijziging.
        */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              'document.querySelectorAll(\'link[rel="stylesheet"]\').forEach(function(l){var m=l.media;l.media="print";l.addEventListener("load",function(){l.media=m||"all"},{once:true})});',
          }}
        />
      </head>
      <body>
        {children}
        <Scripts />
        <noscript>
          <img
            src="https://smart.seojuice.io/pixel"
            width={1}
            height={1}
            alt=""
            style={{ display: "none" }}
          />
        </noscript>
      </body>
    </html>
  );
}

function GoogleAnalyticsTracker() {
  const pathname = useLocation({ select: (location) => location.pathname });

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  const [showVitals, setShowVitals] = useState(false);

  useEffect(() => {
    // Runtime perf-check: logt LCP + hero-image downloads in console.
    void import("@/lib/perf-check").then((m) => m.runPerfCheck());
    // Web Vitals monitoring — logt LCP/CLS/INP/TBT/FCP/TTFB naar console
    // en publiceert ze op window.__webVitals. Geen visuele wijziging.
    void import("@/lib/web-vitals").then((m) => {
      m.startWebVitals();
      setShowVitals(m.isOverlayEnabled());
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <VatProvider>
          <CartProvider>
            <AnnouncerProvider>
              <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
              >
                Direct naar inhoud
              </a>
              {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
              <div id="main-content" tabIndex={-1} className="outline-none">
                <Outlet />
              </div>
              <GoogleAnalyticsTracker />
              <CookieBanner />
              <Suspense fallback={null}>
                <SeoOverrideApplier />
                <TelClickTracker />
                <Toaster richColors position="top-right" />
                {showVitals && <WebVitalsOverlay />}
                <JsonLdValidator />
              </Suspense>
            </AnnouncerProvider>
          </CartProvider>
        </VatProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
